import React, { useState, useRef, useCallback, useEffect } from 'react';

const TOTAL_STEPS = 6;

const STEPS = [
  {
    title: 'Welcome Banner',
    description: 'The green header greets you by name and gives a quick summary. This is your command center \u2014 everything about your business in one scrollable view.',
    highlight: 'headerBand',
    highlightType: 'green',
    scroll: 0,
    buttonText: 'Next \u2192',
  },
  {
    title: 'Fairy AI Coach',
    description: 'Your personal AI business coach analyzes your goals, activities, and deals 24/7. It generates Priority Actions (numbered steps) and Time-Sensitive alerts so you never miss a beat.',
    highlight: 'coach',
    highlightType: 'purple',
    scroll: 0,
    showCoachExpanded: true,
  },
  {
    title: 'Financial Summary',
    description: 'A live snapshot of your finances this month \u2014 net profit, total income vs. expenses, and budget utilization with a progress bar. Tap "View Full P&L" to dive deeper.',
    highlight: 'finSummary',
    highlightType: 'green',
    scroll: 100,
    animateBudget: true,
  },
  {
    title: 'Commission Cap',
    description: 'Track exactly how much you\u2019ve paid toward your broker cap. The progress bar shows your percentage, and you can update it anytime as deals close.',
    highlight: 'capTile',
    highlightType: 'green',
    scroll: 260,
    animateCap: true,
  },
  {
    title: 'My Deals',
    description: 'Your deal pipeline at a glance \u2014 active, closed, and total earned. Each deal shows its closing date and progress dots. Tap "View All Deals" for the full picture.',
    highlight: 'myDeals',
    highlightType: 'green',
    scroll: 340,
  },
  {
    title: 'Quick Actions (+)',
    description: 'The green + button is always accessible. Tap it to quickly Log Activity, Log a Reflection, Add a Deal, or Add an Expense \u2014 without leaving the dashboard.',
    highlight: 'fab',
    highlightType: 'green',
    scroll: 340,
    showFabMenu: true,
    buttonText: 'Finish \u2713',
  },
];

const DashboardTourModal = ({ isOpen, onClose }) => {
  // 'intro' | 1-6 | 'outro'
  const [phase, setPhase] = useState('intro');
  const [budgetWidth, setBudgetWidth] = useState('0%');
  const [capWidth, setCapWidth] = useState('0%');
  const contentRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPhase('intro');
      setBudgetWidth('0%');
      setCapWidth('0%');
    }
  }, [isOpen]);

  // Animate fills when relevant step is active
  useEffect(() => {
    if (typeof phase === 'number') {
      const currentStepData = STEPS[phase - 1];
      
      if (currentStepData.animateBudget) {
        const t = setTimeout(() => setBudgetWidth('69%'), 200);
        return () => clearTimeout(t);
      } else {
        setBudgetWidth('0%');
      }

      if (currentStepData.animateCap) {
        const t = setTimeout(() => setCapWidth('20%'), 200);
        return () => clearTimeout(t);
      } else {
        setCapWidth('0%');
      }
    }
  }, [phase]);

  const startTour = useCallback((e) => {
    e.stopPropagation();
    setPhase(1);
  }, []);

  const nextStep = useCallback(() => {
    setPhase(prev => {
      if (typeof prev === 'number' && prev < TOTAL_STEPS) return prev + 1;
      if (typeof prev === 'number' && prev >= TOTAL_STEPS) return 'outro';
      return prev;
    });
  }, []);

  const handlePhoneClick = useCallback(() => {
    if (typeof phase === 'number' && phase >= 1 && phase <= TOTAL_STEPS) {
      nextStep();
    }
  }, [phase, nextStep]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleWatchAgain = useCallback((e) => {
    e.stopPropagation();
    setPhase('intro');
    setBudgetWidth('0%');
    setCapWidth('0%');
  }, []);

  if (!isOpen) return null;

  const currentStep = typeof phase === 'number' ? STEPS[phase - 1] : null;
  const isStepActive = typeof phase === 'number';

  // Helper: get tile opacity + filter
  const tileStyle = (id) => {
    if (!isStepActive) return { opacity: 0.15, filter: 'none' };
    const step = STEPS[phase - 1];
    if (step.highlight === id) {
      const shadow = step.highlightType === 'purple'
        ? 'drop-shadow(0 0 8px rgba(124,58,237,.12))'
        : 'drop-shadow(0 0 8px rgba(47,161,99,.12))';
      return { opacity: 1, filter: shadow };
    }
    return { opacity: 0.15, filter: 'none' };
  };

  const headerBandExtraStyle = () => {
    if (!isStepActive) return {};
    if (currentStep.highlight === 'headerBand') {
      return { filter: 'drop-shadow(0 0 8px rgba(47,161,99,.2))' };
    }
    return { filter: 'none', opacity: 1 };
  };

  const fabStyle = () => {
    if (!isStepActive) return { opacity: 0.15, filter: 'none' };
    if (currentStep.highlight === 'fab') {
      return { opacity: 1, filter: 'drop-shadow(0 0 8px rgba(47,161,99,.12))' };
    }
    return { opacity: 0.15, filter: 'none' };
  };

  const scrollY = isStepActive ? currentStep.scroll : 0;
  const showCoachExpanded = isStepActive && currentStep.showCoachExpanded;
  const showFabMenu = isStepActive && currentStep.showFabMenu;

  return (
    <div
      data-testid="dashboard-tour-overlay"
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => { e.stopPropagation(); }}
    >
      {/* Close button - outside phone frame */}
      <button
        data-testid="dashboard-tour-close-btn"
        onClick={handleClose}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 10001,
          background: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: '50%',
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: 20,
          cursor: 'pointer',
          fontFamily: "'Poppins',sans-serif",
        }}
      >
        &times;
      </button>

      {/* Phone Frame */}
      <div
        data-testid="dashboard-tour-phone-frame"
        onClick={handlePhoneClick}
        style={{
          width: 375,
          height: 700,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 64px)',
          background: '#f9fafb',
          borderRadius: 32,
          border: '12px solid #222',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 20px 60px rgba(0,0,0,.5)',
          cursor: 'pointer',
          fontFamily: "'Poppins',sans-serif",
        }}
      >
        {/* ===== INTRO SCREEN ===== */}
        <div
          data-testid="dashboard-tour-intro"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 200,
            background: 'linear-gradient(135deg,#092825,#0f3d38)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
            transition: 'opacity .5s',
            opacity: phase === 'intro' ? 1 : 0,
            pointerEvents: phase === 'intro' ? 'auto' : 'none',
          }}
        >
          <div style={{ fontSize: 48, animation: 'tourBob 2s ease-in-out infinite' }}>
            {'\uD83E\uDDDA'}
          </div>
          <h1 style={{ color: '#2FA163', fontSize: 22, fontWeight: 700, fontFamily: "'Poppins',sans-serif" }}>Dashboard Overview</h1>
          <p style={{ color: 'rgba(255,255,255,.5)', fontSize: 12, marginBottom: 8, fontFamily: "'Poppins',sans-serif" }}>Your command center at a glance</p>
          <button
            data-testid="dashboard-tour-start-btn"
            onClick={startTour}
            style={{
              background: '#2FA163', color: '#fff', border: 'none',
              padding: '14px 40px', borderRadius: 12,
              fontFamily: "'Poppins',sans-serif", fontSize: 15, fontWeight: 600,
              cursor: 'pointer', boxShadow: '0 4px 20px rgba(47,161,99,.4)',
            }}
          >
            Start Tour
          </button>
        </div>

        {/* ===== TOP BAR (step counter + caption) ===== */}
        <div
          data-testid="dashboard-tour-top-bar"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100,
            background: 'linear-gradient(135deg,#2FA163 0%,#268f55 100%)',
            padding: '12px 16px 10px',
            opacity: isStepActive ? 1 : 0,
            transition: 'all .3s',
            pointerEvents: 'none',
          }}
        >
          <div style={{ color: 'rgba(255,255,255,.7)', fontSize: 10, fontWeight: 600, marginBottom: 4, letterSpacing: '.5px' }}>
            STEP {typeof phase === 'number' ? phase : ''} OF {TOTAL_STEPS}
          </div>
          <h2 style={{ color: '#fff', fontSize: 13, fontWeight: 600, margin: 0 }}>
            {currentStep?.title || ''}
          </h2>
          <p style={{ color: 'rgba(255,255,255,.85)', fontSize: 10.5, marginTop: 2, lineHeight: 1.4 }}>
            {currentStep?.description || ''}
          </p>
        </div>

        {/* ===== NEXT BUTTON ===== */}
        {isStepActive && (
          <button
            data-testid="dashboard-tour-next-btn"
            onClick={(e) => { e.stopPropagation(); nextStep(); }}
            style={{
              position: 'absolute', bottom: 16, left: 16, right: 16, zIndex: 100,
              background: '#2FA163', color: '#fff', border: 'none',
              borderRadius: 12, padding: 14,
              fontFamily: "'Poppins',sans-serif", fontSize: 14, fontWeight: 600,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 20px rgba(47,161,99,.4)',
            }}
          >
            {currentStep?.buttonText || 'Next \u2192'}
          </button>
        )}

        {/* ===== SCROLLABLE CONTENT ===== */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
          <div
            ref={contentRef}
            style={{
              padding: '0 0 80px',
              transition: 'transform .6s ease',
              transform: `translateY(-${scrollY}px)`,
            }}
          >
            {/* Green header band */}
            <div
              style={{
                background: 'linear-gradient(135deg,#2FA163,#268f55)',
                padding: '100px 20px 20px',
                textAlign: 'center',
                position: 'relative',
                transition: 'all .5s',
                ...headerBandExtraStyle(),
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 6 }}>{'\u2728'}</div>
              <h1 style={{ color: '#fff', fontSize: 18, fontWeight: 700, margin: 0, fontFamily: "'Poppins',sans-serif" }}>Welcome back, Luke!</h1>
              <div style={{ color: 'rgba(255,255,255,.8)', fontSize: 11, marginTop: 2 }}>Here's your business at a glance</div>
            </div>

            {/* Fairy AI Coach tile */}
            <div style={{
              background: '#fff', borderRadius: 14, margin: '8px 12px', padding: 16,
              boxShadow: '0 1px 3px rgba(0,0,0,.06)', border: '1px solid rgba(0,0,0,.04)',
              transition: 'all .5s',
              ...tileStyle('coach'),
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, background: '#f3e8ff', color: '#7C3AED' }}>{'\u2728'}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', flex: 1 }}>Fairy AI Coach</div>
                <div style={{ color: '#9ca3af', fontSize: 14 }}>{'\u25BE'}</div>
              </div>
              <div style={{ color: '#6b7280', fontSize: 11, marginTop: -8, marginBottom: 10 }}>AI-powered insights</div>
              <button style={{ width: '100%', padding: 10, border: '1.5px solid #2FA163', background: 'transparent', borderRadius: 10, color: '#2FA163', fontFamily: "'Poppins',sans-serif", fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Generate New Insights</button>

              {/* Coach expanded sections - only shown on step 2 */}
              {showCoachExpanded && (
                <>
                  <div style={{ marginTop: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, background: '#ecfdf5', color: '#2FA163' }}>{'\uD83C\uDFAF'}</div>
                      <h4 style={{ fontSize: 12, fontWeight: 600, color: '#111827', margin: 0 }}>Priority Actions</h4>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 5, fontSize: 10, color: '#374151', lineHeight: 1.4 }}>
                      <span style={{ color: '#2FA163', fontWeight: 700, minWidth: 14 }}>1.</span>
                      <span>Focus on lead generation to fill your pipeline; your current activity isn't translating into closings yet.</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 5, fontSize: 10, color: '#374151', lineHeight: 1.4 }}>
                      <span style={{ color: '#2FA163', fontWeight: 700, minWidth: 14 }}>2.</span>
                      <span>Increase your social media marketing efforts; it's a gap you've identified and could drive more leads.</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 5, fontSize: 10, color: '#374151', lineHeight: 1.4 }}>
                      <span style={{ color: '#2FA163', fontWeight: 700, minWidth: 14 }}>3.</span>
                      <span>Complete the next milestones for your active deals to ensure they close on time.</span>
                    </div>
                  </div>
                  <div style={{ marginTop: 8, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, background: '#fff7ed', color: '#ea580c' }}>{'\u26A0\uFE0F'}</div>
                      <h4 style={{ fontSize: 12, fontWeight: 600, color: '#111827', margin: 0 }}>Time-Sensitive</h4>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 5, fontSize: 10, color: '#374151', lineHeight: 1.4 }}>
                      <span style={{ color: '#ea580c', minWidth: 14 }}>{'\u26A0\uFE0F'}</span>
                      <span>Get the escrow money deposited for 115 Wright St and 1515 Dodo St {'\u2014'} that's your next step to keep those deals moving.</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 5, fontSize: 10, color: '#374151', lineHeight: 1.4 }}>
                      <span style={{ color: '#ea580c', minWidth: 14 }}>{'\u26A0\uFE0F'}</span>
                      <span>Prepare for the appraisal on 1313 High St to avoid any delays as it approaches closing.</span>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Financial Summary tile */}
            <div style={{
              background: '#fff', borderRadius: 14, margin: '8px 12px', padding: 16,
              boxShadow: '0 1px 3px rgba(0,0,0,.06)', border: '1px solid rgba(0,0,0,.04)',
              transition: 'all .5s',
              ...tileStyle('finSummary'),
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, background: '#ecfdf5', color: '#2FA163' }}>$</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', flex: 1 }}>Financial Summary</div>
                <div style={{ color: '#9ca3af', fontSize: 14 }}>{'\u203A'}</div>
              </div>
              <div style={{ color: '#6b7280', fontSize: 10, marginBottom: 2 }}>This Month's Net</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>$41,783</div>
                <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 500 }}>MTD</div>
              </div>
              <div style={{ display: 'flex', gap: 20, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 9, color: '#6b7280' }}>Total Income</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#2FA163' }}>$43,903</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: '#6b7280' }}>Total Expenses</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#ef4444' }}>$2,120</div>
                </div>
              </div>
              <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 4 }}>Budget Utilization</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', minWidth: 36 }}>69%</div>
                <div style={{ flex: 1, height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: 'linear-gradient(90deg,#2FA163,#34d399)', borderRadius: 4, transition: 'width 1s ease', width: budgetWidth }} />
                </div>
              </div>
              <button style={{ width: '100%', padding: 10, border: '1.5px solid #e5e7eb', background: 'transparent', borderRadius: 10, color: '#374151', fontFamily: "'Poppins',sans-serif", fontSize: 11, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>{'\uD83D\uDCCA'} View Full P&L</button>
            </div>

            {/* Commission Cap tile */}
            <div style={{
              background: '#fff', borderRadius: 14, margin: '8px 12px', padding: 16,
              boxShadow: '0 1px 3px rgba(0,0,0,.06)', border: '1px solid rgba(0,0,0,.04)',
              transition: 'all .5s',
              ...tileStyle('capTile'),
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, background: '#ecfdf5', color: '#2FA163' }}>{'\uD83C\uDFAF'}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', flex: 1 }}>Commission Cap</div>
                <div style={{ background: '#ecfdf5', color: '#2FA163', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>20%</div>
                <div style={{ color: '#9ca3af', fontSize: 14 }}>{'\u203A'}</div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: 'linear-gradient(90deg,#2FA163,#34d399)', borderRadius: 4, transition: 'width 1s ease', width: capWidth }} />
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>80% to cap</div>
              <button style={{ width: '100%', padding: 10, border: '1.5px solid #e5e7eb', background: 'transparent', borderRadius: 10, color: '#374151', fontFamily: "'Poppins',sans-serif", fontSize: 11, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>Update Cap Progress</button>
            </div>

            {/* My Deals tile */}
            <div style={{
              background: '#fff', borderRadius: 14, margin: '8px 12px', padding: 16,
              boxShadow: '0 1px 3px rgba(0,0,0,.06)', border: '1px solid rgba(0,0,0,.04)',
              transition: 'all .5s',
              ...tileStyle('myDeals'),
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, background: '#ecfdf5', color: '#2FA163' }}>{'\uD83C\uDFE0'}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', flex: 1 }}>My Deals</div>
                <div style={{ color: '#9ca3af', fontSize: 14 }}>{'\u203A'}</div>
              </div>
              {/* Stats row */}
              <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center', paddingBottom: 10, marginBottom: 10, borderBottom: '1px solid #f3f4f6' }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>6</div>
                  <div style={{ fontSize: 9, color: '#6b7280' }}>Active</div>
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>3</div>
                  <div style={{ fontSize: 9, color: '#6b7280' }}>Closed</div>
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#2FA163' }}>$87K</div>
                  <div style={{ fontSize: 9, color: '#6b7280' }}>Earned</div>
                </div>
              </div>
              {/* Deal rows */}
              <div style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>432 West Longstreet</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>Closing: 05/28/26</div>
                  <div style={{ display: 'flex', gap: 3 }}>
                    {[1,1,1,1,0,0].map((filled, i) => (
                      <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: filled ? '#2FA163' : '#e5e7eb' }} />
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ padding: '8px 0' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>980 Gunner Row</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>Closing: 05/22/26</div>
                  <div style={{ display: 'flex', gap: 3 }}>
                    {[1,1,1,1,1,1].map((filled, i) => (
                      <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: filled ? '#2FA163' : '#e5e7eb' }} />
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 10, padding: '6px 0' }}>+4 more active deals</div>
              <div style={{ textAlign: 'center', color: '#2FA163', fontSize: 11, fontWeight: 600, padding: '4px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                View All Deals <span>{'\u203A'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== FAB ===== */}
        <div
          style={{
            position: 'absolute', bottom: 60, right: 16, width: 48, height: 48,
            background: '#2FA163', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 24, fontWeight: 300,
            boxShadow: '0 4px 12px rgba(47,161,99,.4)', zIndex: 50,
            transition: 'all .5s',
            ...fabStyle(),
          }}
        >
          {showFabMenu ? '\u2715' : '+'}
        </div>

        {/* ===== FAB MENU ===== */}
        <div
          style={{
            position: 'absolute', bottom: 115, right: 16, zIndex: 50,
            display: 'flex', flexDirection: 'column', gap: 8,
            opacity: showFabMenu ? 1 : 0,
            pointerEvents: showFabMenu ? 'auto' : 'none',
            transition: 'all .4s',
          }}
        >
          {[
            ['\u2705', 'Log Activity'],
            ['\uD83D\uDCDD', 'Log a Reflection'],
            ['\uD83D\uDCB2', 'Add Deal'],
            ['\u3030\uFE0F', 'Add Expense'],
          ].map(([icon, label], i) => (
            <div key={i} style={{
              background: '#fff', borderRadius: 12, padding: '12px 18px',
              boxShadow: '0 2px 8px rgba(0,0,0,.1)',
              display: 'flex', alignItems: 'center', gap: 10,
              fontSize: 12, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap',
            }}>
              <span style={{ fontSize: 14 }}>{icon}</span> {label}
            </div>
          ))}
        </div>

        {/* ===== TAB BAR ===== */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 40,
          background: '#fff', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-around', padding: '6px 0 10px',
        }}>
          {[
            ['\uD83C\uDFE0', 'Overview', true],
            ['\uD83E\uDDEE', 'Calculators', false],
            ['\uD83D\uDCB0', 'Finances', false],
            ['\u2705', 'Actions', false],
            ['\u2630', 'More', false],
          ].map(([icon, label, active], i) => (
            <div key={i} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
              fontSize: 8, color: active ? '#2FA163' : '#9ca3af', fontWeight: 500,
            }}>
              <div style={{ fontSize: 14 }}>{icon}</div>
              {label}
            </div>
          ))}
        </div>

        {/* ===== OUTRO SCREEN ===== */}
        <div
          data-testid="dashboard-tour-outro"
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 200,
            background: 'linear-gradient(135deg,#092825ee,#0f3d38ee)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: phase === 'outro' ? 1 : 0,
            pointerEvents: phase === 'outro' ? 'auto' : 'none',
            transition: 'opacity .5s',
          }}
        >
          <div style={{ fontSize: 44 }}>{'\u2705'}</div>
          <h2 style={{ color: '#2FA163', fontSize: 17, fontWeight: 700, margin: 0, fontFamily: "'Poppins',sans-serif" }}>Your Business at a Glance</h2>
          <p style={{ color: 'rgba(255,255,255,.5)', fontSize: 11, textAlign: 'center', padding: '0 36px', lineHeight: 1.45, fontFamily: "'Poppins',sans-serif" }}>
            AI coaching, financial tracking, deal pipeline, and quick actions {'\u2014'} your entire business in one view.
          </p>
          <button
            data-testid="dashboard-tour-watch-again-btn"
            onClick={handleWatchAgain}
            style={{
              marginTop: 10, background: '#2FA163', color: '#fff', border: 'none',
              padding: '12px 28px', borderRadius: 10,
              fontFamily: "'Poppins',sans-serif", fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Watch Again
          </button>
        </div>
      </div>

      {/* Keyframes for bob animation */}
      <style>{`
        @keyframes tourBob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-7px); }
        }
      `}</style>
    </div>
  );
};

export default DashboardTourModal;
